import React, { useState, useRef, useEffect } from 'react';
import { useBus, useListener as eventListener } from 'react-bus';
import { useDispatch } from 'react-redux';

import { Avatar } from "@progress/kendo-react-layout";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { ComboBox } from "@progress/kendo-react-dropdowns";

import AboutDialog from "views/user/about/AboutDialog.jsx";

import { addMessage } from '../../messages/messageSlice';
import useSession from '../../hooks/useSession';

export default function UserMenu(props) {

	const { navigateTo, unreadMessages, sessionChanging, sessionChanged } = props;

	const menuRef = useRef();
	const popupRef = useRef(null);
	const initState = {
		showMenu: false
	};
	const [state, setState] = useState(initState);
	const [aboutDialogVisible, setAboutDialogVisible] = useState(false);
	const { getSession, setSession, persistPreferences } = useSession();
	const session = getSession();
	const eventBus = useBus();
	const dispatch = useDispatch();

	const avatarId = session && session.user ? session.user.avatarId : null;
	const avatar = avatarId
		? `${API_URL}/public/avatar?id=${avatarId}`
		: `${API_URL}/images/profile-icon.png`;

	const handleMenuClick = (e) => {
		if (aboutDialogVisible) return;

		e.stopPropagation();
		setState((currentState) => ({
			...currentState,
			showMenu: !currentState.showMenu
		}));
		eventBus.emit('documentClicked');
	};
	
	const handleMenuKeyDown = (e) => {
		if (e.target !== e.currentTarget) {
			return;
		}

		if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'ArrowDown') {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		setState((currentState) => ({
			...currentState,
			showMenu: !currentState.showMenu
		}));

		eventBus.emit('documentClicked');
	};

	const handleMenuBlur = () => {
		requestAnimationFrame(() => {
			const focusedElement = document.activeElement;
			const focusIsInMenu =
				menuRef.current && menuRef.current.contains(focusedElement);
			const focusIsInPopup =
				popupRef.current && popupRef.current.contains(focusedElement);

			if (!focusIsInMenu && !focusIsInPopup) {
				setState((currentState) => ({
					...currentState,
					showMenu: false
				}));
			}
		});
	};

	const handleSelect = (e) => {
		setState((currentState) => ({
			...currentState,
			showMenu: false
		}));

		if (e.item.text === 'Manage Profile') {
			navigateTo('/profile', { replace: true });
		} else if (e.item.text === 'Messages') {
			navigateTo('/messages', { replace: true });
		} else if (e.item.text === 'Help') {
			console.log("Help");
		} else if (e.item.text === 'About') {
			setAboutDialogVisible(true);
		} else if (e.item.text === 'Log Off') {
			persistPreferences();
		} else {
			console.log("invalid menu option");
		}
	};

	const documentClickListener = () => {
		if (state.showMenu) {
			setState((currentState) => ({
				...currentState,
				showMenu: false
			}));
		}
	};

	useEffect(() => {
		document.addEventListener("click", documentClickListener);

		if (state.showMenu && popupRef.current) {
			popupRef.current.focus();
		}

		return () => {
			document.removeEventListener("click", documentClickListener);
		};
	}, [state.showMenu]);

	eventListener('documentClicked', () => {
		if (state.showMenu) {
			setState((currentState) => ({
				...currentState,
				showMenu: false
			}));
		}
	});

	const organizationChanged = (e, org) => {
		handleMenuClick(e);

		if (org.id === session.organization.id) {
			return;
		}

		sessionChanging();
		switchToOrg(org);
	};

	const switchToOrg = (org) => {
		fetch(API_URL + "/api/session/organization?id=" + org.id, {
			method: 'PUT',
			credentials: 'include'
		}).then(function(response) {
			if (!response.ok) throw new Error(response.status);
			return response.json();
		}).then(function(newSession) {
			persistPreferences(true, () => {
				setSession(newSession, true);
				window.location.reload();
			});
		}).catch(function(err) {
			sessionChanged();
			const errMsg = err.message === '403'
				? "Unauthorized session!"
				: "Failed to switch roles, please contact Support.";
			dispatch(addMessage({ type: 'error', msg: errMsg }));
		});
	};

	const possibleOrgs = Object.keys(session.orgsWithRoles)
		.map((orgId) => session.orgsWithRoles[orgId])
		.sort((a, b) => {
			const fa = a.name.toLowerCase();
			const fb = b.name.toLowerCase();

			if (fa < fb) return -1;
			return 1;
		});

	const currentOrg = possibleOrgs.filter(
		(org) => org.id === session.organization.id
	)[0];

	const renderOrganizationPicker = () => {
		return (
			<div onClick={(e) => e.stopPropagation()}>
				<ComboBox
					className="constituo-organization-picker"
					id="organization-picker"
					textField="name"
					data={possibleOrgs}
					dataItemKey="id"
					value={session.organization}
					title={session.organization.name}
					clearButton={false}
					itemRender={renderOrganization}
					popupSettings={{ width: 300 }}
				/>
			</div>
		);
	};

	const renderOrganization = (elem, itemProps) => {
		return (
			<li
				className="constituo-organization-picker-item"
				title={itemProps.dataItem.name}
				onClick={(e) => organizationChanged(e, itemProps.dataItem)}
			>
				{itemProps.dataItem.name}
			</li>
		);
	};

	return (
		<div
			className="user-menu k-hbox"
			ref={menuRef}
			tabIndex={props.tabIndex}
			role="button"
			aria-haspopup="menu"
			aria-expanded={state.showMenu}
			aria-describedby="user-menu-descriptor"
			onClick={handleMenuClick}
			onKeyDown={handleMenuKeyDown}
			onBlur={handleMenuBlur}
		>
			<span
				id="user-menu-descriptor"
				className="constituo-visually-hidden"
			>
				User Menu
			</span>

			<Avatar
				shape="circle"
				type="image"
			>
				<img
					className="constituo-profile-image"
					alt="avatar"
					src={avatar}
				/>

				<div
					style={{
						display: unreadMessages ? "block" : "none",
						backgroundColor: "#C1493C",
						fontSize: "11px",
						position: "absolute",
						fontWeight: "bold",
						right: "8px",
						top: "36px",
						width: "14px",
						height: "14px",
						borderRadius: "7px",
						textAlign: "center",
						verticalAlign: "middle",
						lineHeight: "14px"
					}}
				>
					{unreadMessages}
				</div>
			</Avatar>

			{aboutDialogVisible && (
				<AboutDialog
					okCallback={() => setAboutDialogVisible(false)}
				/>
			)}

			<Popup
				anchor={menuRef.current}
				show={state.showMenu}
				style={{ marginLeft: -10 }}
				className="constituo-user-menu"
			>
				<div
					ref={popupRef}
					tabIndex="0"
					onBlur={handleMenuBlur}
				>
					<Menu
						vertical={true}
						style={{ display: "inline-block" }}
						onSelect={handleSelect}
					>
						{possibleOrgs.length > 1 && (
							<MenuItem
								icon="fa fa-fw fa-building"
								text={currentOrg.name}
								render={renderOrganizationPicker}
								cssClass="constituo-popup-menu-item"
								tabIndex="0"
							/>
						)}

						<MenuItem
							icon="fa fa-fw fa-street-view"
							text="Manage Profile"
							cssClass="constituo-popup-menu-item"
							tabIndex="0"
						/>

						<MenuItem
							icon="fa fa-fw fa-mail"
							text="Messages"
							cssClass="constituo-popup-menu-item"
							tabIndex="0"
						/>

						<MenuItem
							icon="fa fa-fw fa-help"
							text="Help"
							cssClass="constituo-popup-menu-item"
							tabIndex="0"
						/>

						<MenuItem
							icon="fa fa-fw fa-info-circled"
							text="About"
							cssClass="constituo-popup-menu-item"
							tabIndex="0"
						/>

						<MenuItem
							icon="fa fa-fw fa-logout"
							text="Log Off"
							cssClass="constituo-popup-menu-item"
							tabIndex="0"
						/>
					</Menu>
				</div>
			</Popup>
		</div>
	);
}